import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Select } from '@/shared/components/ui/Select';
import { Switch } from '@/shared/components/ui/Switch';
import { RadioGroup } from '@/shared/components/ui/RadioGroup';
import { emailDomainsApi } from '@/modules/email-domains/services';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import {
  EMAIL_TICKET_CATEGORY_OPTIONS,
  EMAIL_TICKET_PRIORITY_OPTIONS,
  TICKET_CREATION_MODE_OPTIONS,
} from '../../constants/emailTicketOptions';
import { Icon } from '@/shared/icons'
/**
 * Formulario para criar canal de e-mail via Resend.
 *
 * Busca dominios verificados do endpoint /email-domains/dropdown e
 * permite configurar endereco, nome de exibicao, assinatura, modo de
 * criacao de ticket (Plano B — Email→Ticket) e resposta automatica.
 *
 * @example
 * <EmailResendForm onComplete={(data) => handleAuthComplete(data)} />
 */
export interface EmailResendFormProps {
  /** Callback quando a configuracao esta completa */
  onComplete: (data: EmailResendData) => void;
}

export type EmailTicketCreationMode = 'off' | 'review' | 'auto';
export type EmailTicketPriorityOption = 'low' | 'medium' | 'high' | 'urgent';

export interface EmailResendData {
  providerId: string;
  name: string;
  identifier: string;
  config: {
    domainId: string;
    emailAddress: string;
    displayName: string;
    signature?: string;
    /**
     * Modo de criação de ticket a partir de e-mail inbound (Plano B).
     * Mesmo shape do PATCH /channels/:id/email-config — a tela de edição
     * (EmailResendConfig.tsx) converge para o mesmo copy/opções.
     */
    ticketCreationMode?: EmailTicketCreationMode;
    ticketCategory?: string;
    ticketPriority?: EmailTicketPriorityOption;
    ticketTeamId?: string;
    autoReplyEnabled?: boolean;
    autoReplyMessage?: string;
  };
  credentials: Record<string, never>;
}

export const EmailResendForm: React.FC<EmailResendFormProps> = ({
  onComplete,
}) => {
  // State do formulario
  const [channelName, setChannelName] = useState('');
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [emailPrefix, setEmailPrefix] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [signature, setSignature] = useState('');
  const [ticketCreationMode, setTicketCreationMode] = useState<EmailTicketCreationMode>('off');
  const [ticketCategory, setTicketCategory] = useState('');
  const [ticketPriority, setTicketPriority] = useState<EmailTicketPriorityOption | ''>('');
  const [ticketTeamId, setTicketTeamId] = useState('');
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyMessage, setAutoReplyMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Buscar dominios verificados para dropdown
  const { data: domains = [], isLoading: domainsLoading } = useQuery({
    queryKey: ['email-domains', 'dropdown'],
    queryFn: () => emailDomainsApi.getForDropdown(),
    staleTime: 60_000,
  });

  // Equipe destino dos tickets criados a partir de e-mails (mesma fonte da
  // tela de edição — useTeamsDropdown)
  const { data: teams = [] } = useTeamsDropdown();
  const teamOptions = [
    { value: '', label: 'Sem equipe (distribuição automática)' },
    ...teams.map((team) => ({ value: team.id, label: team.name })),
  ];

  // Apenas dominios verificados
  const verifiedDomains = domains.filter((d) => d.status === 'verified');

  // Selecionar primeiro dominio verificado automaticamente
  useEffect(() => {
    if (verifiedDomains.length > 0 && !selectedDomainId) {
      setSelectedDomainId(verifiedDomains[0].id);
    }
  }, [verifiedDomains, selectedDomainId]);

  // Computar dominio selecionado
  const selectedDomain = verifiedDomains.find((d) => d.id === selectedDomainId);
  const fullEmail = emailPrefix && selectedDomain
    ? `${emailPrefix}@${selectedDomain.domain}`
    : '';

  const handleSubmit = () => {
    // Validacoes
    if (!channelName || channelName.trim().length < 2) {
      setErrorMessage('Nome do canal deve ter pelo menos 2 caracteres');
      return;
    }

    if (!selectedDomainId) {
      setErrorMessage('Selecione um dominio verificado');
      return;
    }

    if (!emailPrefix || emailPrefix.trim().length === 0) {
      setErrorMessage('Informe o prefixo do e-mail');
      return;
    }

    // Validar prefixo (sem caracteres especiais invalidos)
    const prefixRegex = /^[a-zA-Z0-9._-]+$/;
    if (!prefixRegex.test(emailPrefix)) {
      setErrorMessage('O prefixo do e-mail contém caracteres inválidos');
      return;
    }

    if (!displayName || displayName.trim().length === 0) {
      setErrorMessage('Informe o nome de exibição');
      return;
    }

    if (autoReplyEnabled && (!autoReplyMessage || autoReplyMessage.trim().length === 0)) {
      setErrorMessage('Informe a mensagem de resposta automática');
      return;
    }

    setErrorMessage(null);

    onComplete({
      providerId: 'email-resend',
      name: channelName.trim(),
      identifier: fullEmail,
      config: {
        domainId: selectedDomainId,
        emailAddress: fullEmail,
        displayName: displayName.trim(),
        signature: signature.trim() || undefined,
        ticketCreationMode,
        ticketCategory: ticketCreationMode !== 'off' ? (ticketCategory || undefined) : undefined,
        ticketPriority: ticketCreationMode !== 'off' ? (ticketPriority || undefined) : undefined,
        ticketTeamId: ticketCreationMode !== 'off' ? (ticketTeamId || undefined) : undefined,
        autoReplyEnabled,
        autoReplyMessage: autoReplyEnabled ? autoReplyMessage.trim() : undefined,
      },
      credentials: {},
    });
  };

  // Estado de carregamento dos dominios
  if (domainsLoading) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center mb-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center animate-pulse"
            style={{ backgroundColor: 'var(--theme-button-primary)' }}
          >
            <Icon name="mail" size={32} className="text-white" />
          </div>
        </div>
        <p
          className="text-lg font-medium"
          style={{ color: '#1e293b' }}
        >
          Carregando domínios...
        </p>
      </div>
    );
  }

  // Nenhum dominio verificado
  if (verifiedDomains.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center mb-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#fef2f2' }}
          >
            <Icon name="alert-circle" size={32} style={{ color: '#ef4444' }} />
          </div>
        </div>
        <p
          className="text-lg font-medium mb-2"
          style={{ color: '#1e293b' }}
        >
          Nenhum domínio verificado
        </p>
        <p
          className="text-sm mb-6"
          style={{ color: '#64748b' }}
        >
          Cadastre e verifique um domínio em Configurações &gt; Domínios de E-mail antes de criar um canal.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Nome do Canal */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: '#1e293b' }}
        >
          Nome do Canal *
        </label>
        <Input
          type="text"
          value={channelName}
          onChange={(e) => setChannelName(e.target.value)}
          placeholder="Ex: E-mail Suporte"
          className="w-full"
        />
      </div>

      {/* Dominio */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: '#1e293b' }}
        >
          Domínio *
        </label>
        <Select
          value={selectedDomainId}
          onChange={(value) => setSelectedDomainId(value)}
          options={verifiedDomains.map((d) => ({
            value: d.id,
            label: d.domain,
          }))}
        />
        <p
          className="text-xs mt-1"
          style={{ color: '#64748b' }}
        >
          Apenas domínios com DNS verificado são exibidos
        </p>
      </div>

      {/* Endereco de E-mail */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: '#1e293b' }}
        >
          Endereço de E-mail *
        </label>
        <div className="flex items-center gap-0">
          <Input
            type="text"
            value={emailPrefix}
            onChange={(e) => setEmailPrefix(e.target.value.toLowerCase())}
            placeholder="suporte"
            className="flex-1 rounded-r-none"
          />
          <span
            className="inline-flex items-center px-3 py-2 border border-l-0 rounded-r-lg text-sm"
            style={{
              backgroundColor: '#f1f5f9',
              borderColor: '#d1d5db',
              color: '#64748b',
            }}
          >
            @{selectedDomain?.domain || '...'}
          </span>
        </div>
        {fullEmail && (
          <p
            className="text-xs mt-1 flex items-center gap-1"
            style={{ color: '#64748b' }}
          >
            <Icon name="check-circle" size={12} style={{ color: '#22c55e' }} />
            {fullEmail}
          </p>
        )}
      </div>

      {/* Nome de Exibicao */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: '#1e293b' }}
        >
          Nome de Exibição *
        </label>
        <Input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Equipe de Suporte"
          className="w-full"
        />
        <p
          className="text-xs mt-1"
          style={{ color: '#64748b' }}
        >
          Nome que aparecerá como remetente nos e-mails
        </p>
      </div>

      {/* Assinatura */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: '#1e293b' }}
        >
          Assinatura (HTML)
        </label>
        <Textarea
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          placeholder="<p>Atenciosamente,<br/>Equipe de Suporte</p>"
          rows={3}
        />
        <p
          className="text-xs mt-1"
          style={{ color: '#64748b' }}
        >
          Opcional - Assinatura HTML adicionada ao final de cada e-mail
        </p>
      </div>

      {/* Criação de tickets — Plano B (Email→Ticket), mesmo copy da tela de edição */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{ color: '#1e293b' }}
        >
          Criação de tickets
        </label>
        <div data-testid="channel-email-create-creation-mode">
          <RadioGroup
            name="channel-email-create-ticket-creation-mode"
            options={TICKET_CREATION_MODE_OPTIONS}
            value={ticketCreationMode}
            onChange={(value) => setTicketCreationMode(value as EmailTicketCreationMode)}
            variant="cards"
          />
        </div>

        {ticketCreationMode !== 'off' && (
          <>
            <div
              className="flex items-start gap-2 p-3 mt-3 rounded-lg"
              style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}
              data-testid="channel-email-create-creation-hint"
            >
              <Icon name="info" size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#0284c7' }} />
              <p className="text-xs" style={{ color: '#64748b' }}>
                Com criação ativa, e-mails recebidos viram tickets e não aparecem no chat.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              <Select
                label="Categoria"
                value={ticketCategory}
                onChange={setTicketCategory}
                options={EMAIL_TICKET_CATEGORY_OPTIONS}
                data-testid="channel-email-create-ticket-category"
              />
              <Select
                label="Prioridade"
                value={ticketPriority}
                onChange={(value) => setTicketPriority(value as EmailTicketPriorityOption)}
                options={EMAIL_TICKET_PRIORITY_OPTIONS}
                placeholder="Selecione"
                data-testid="channel-email-create-ticket-priority"
              />
              <Select
                label="Equipe"
                value={ticketTeamId}
                onChange={setTicketTeamId}
                options={teamOptions}
                data-testid="channel-email-create-ticket-team"
              />
            </div>
          </>
        )}
      </div>

      {/* Auto Reply */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p
              className="text-sm font-medium"
              style={{ color: '#1e293b' }}
            >
              Resposta automática
            </p>
            <p
              className="text-xs"
              style={{ color: '#64748b' }}
            >
              Envia uma resposta automática quando um novo e-mail é recebido
            </p>
          </div>
          <Switch
            checked={autoReplyEnabled}
            onChange={setAutoReplyEnabled}
          />
        </div>

        {autoReplyEnabled && (
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Mensagem de Resposta Automática *
            </label>
            <Textarea
              value={autoReplyMessage}
              onChange={(e) => setAutoReplyMessage(e.target.value)}
              placeholder="Olá! Recebemos seu e-mail e entraremos em contato em breve."
              rows={3}
            />
          </div>
        )}
      </div>

      {/* Info Box */}
      <div
        className="p-4 rounded-lg"
        style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
        }}
      >
        <p
          className="text-sm font-medium mb-1"
          style={{ color: '#1e293b' }}
        >
          Como funciona o E-mail nativo?
        </p>
        <div
          className="text-xs space-y-1"
          style={{ color: '#64748b' }}
        >
          <p>1. Os e-mails são enviados e recebidos com seu domínio próprio.</p>
          <p>2. O domínio precisa estar verificado (DNS configurado) para funcionar.</p>
          <p>3. As respostas dos clientes chegam automaticamente como mensagens na conversa.</p>
        </div>
      </div>

      {/* Erro */}
      {errorMessage && (
        <div
          className="p-3 rounded-lg"
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
          }}
        >
          <p
            className="text-sm"
            style={{ color: '#ef4444' }}
          >
            {errorMessage}
          </p>
        </div>
      )}

      {/* Submit */}
      <Button
        onClick={handleSubmit}
        className="w-full"
      >
        Criar Canal de E-mail
      </Button>
    </div>
  );
};

EmailResendForm.displayName = 'EmailResendForm';
